import React from 'react';
import { CheckOutlined } from '@ant-design/icons';

import { buildPalette } from '../../config/themes';
import { useOptionalTheme } from '../../contexts/ThemeContext';

/**
 * One theme, drawn in the mode the app is currently showing: the rail, a
 * panel on the ground, a line of ink, and the accent. A palette is easier
 * to choose from a small picture of the app than from six color chips, and
 * previewing in the live mode means a dark-mode user picks between dark
 * palettes rather than guessing from their light versions.
 */
function ThemeSwatch({ option, mode, selected, onSelect }) {
	const p = buildPalette(option.value, mode);
	return (
		<button
			type='button'
			className={`theme-swatch${selected ? ' theme-swatch--selected' : ''}`}
			onClick={() => onSelect(option.value)}
			aria-pressed={selected}
			aria-label={`${option.label} — ${option.description}`}
			title={option.description}
		>
			<span
				className='theme-swatch__preview'
				style={{ background: p['--ground'], borderColor: p['--border'] }}
				aria-hidden='true'
			>
				<span className='theme-swatch__rail' style={{ background: p['--rail-bg'], borderColor: p['--border'] }}>
					<span className='theme-swatch__dot' style={{ background: p['--action'] }} />
					<span className='theme-swatch__bar' style={{ background: p['--chip-bg'] }} />
					<span className='theme-swatch__bar' style={{ background: p['--chip-bg'] }} />
				</span>
				<span className='theme-swatch__panel' style={{ background: p['--surface'], borderColor: p['--border'] }}>
					<span className='theme-swatch__line' style={{ background: p['--ink'] }} />
					<span className='theme-swatch__line theme-swatch__line--short' style={{ background: p['--ink-muted'] }} />
					<span className='theme-swatch__pill' style={{ background: p['--action'], color: p['--on-action'] }} />
				</span>
			</span>
			<span className='theme-swatch__name'>
				{option.label}
				{selected && <CheckOutlined className='theme-swatch__check' />}
			</span>
		</button>
	);
}

export default function ThemePicker({ options, value, onChange }) {
	const themeCtx = useOptionalTheme();
	const mode = (themeCtx && themeCtx.mode) || 'light';

	return (
		<div className='theme-picker' role='group' aria-label='Theme'>
			{options.map(option => (
				<ThemeSwatch
					key={option.value}
					option={option}
					mode={mode}
					selected={option.value === value}
					onSelect={onChange}
				/>
			))}
		</div>
	);
}
